import { Checkbox } from "@material-tailwind/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MTButton } from "../../components/button/MTButton";
import ErrorMain from "../../components/error/Error.Main";
import {
  InputMain,
  InputPasswordMain,
} from "../../components/input/InputMain";
import { PopupMain } from "../../components/popup/PopupMain";
import { ICON_EYE, ICON_EYE_CLOSE } from "../../utils/useIcons";
import { loginWithUsername } from "./login.api";
import {
  getLoginErrorMessage,
  validateUsernameLogin,
} from "./login.validation";
import { useProfileSession } from "./use-profile-session";

// รับ Username/Password, ยืนยันผ่าน backend แล้ว restore session ด้วย owner เดียวกับ Google Login
const UsernameLoginPage = () => {
  const navigate = useNavigate();
  const { loadProfileSession } = useProfileSession();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [isPolicyAccepted, setPolicyAccepted] = useState(false);
  const [isPolicyAlertOpen, setPolicyAlertOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState("");
  const [loading, setLoading] = useState(false);

  // ตรวจ form และ policy ก่อนเรียก API; session จะเปลี่ยนเฉพาะหลัง credential และ Storage สำเร็จครบ
  const handleSubmit = async (event) => {
    event.preventDefault();
    setRequestError("");
    const validation = validateUsernameLogin({ username, password });
    setErrors(validation.errors);
    if (Object.keys(validation.errors).length > 0) return;
    if (!isPolicyAccepted) {
      setPolicyAlertOpen(true);
      return;
    }

    setLoading(true);
    try {
      const profile = await loginWithUsername({
        username: validation.username,
        password: validation.password,
      });
      const { destination } = await loadProfileSession(profile);
      navigate(destination);
    } catch (error) {
      setRequestError(getLoginErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-IBMPlexSansThai flex flex-col justify-between relative min-h-screen mx-auto max-w-3xl">
      <div className="relative pt-[105px] px-[32px] pb-[48px]">
        <img
          src="/images/circle_login1.svg"
          className="absolute top-0 right-0"
          alt=""
        />
        <div className="text-maincyan font-bold text-5xl leading-relaxed">
          เข้าสู่ระบบ
        </div>
        <div className="text-mainblue text-base mt-[48px] mb-[24px] text-center">
          เข้าสู่ระบบด้วย Username และ Password
        </div>

        <form
          className="w-full max-w-[350px] mx-auto flex flex-col gap-[15px]"
          onSubmit={handleSubmit}
          noValidate
        >
          <label className="text-mainblue font-semibold" htmlFor="username">
            ชื่อผู้ใช้
          </label>
          <div className="-mt-[13px]">
            <InputMain
              id="username"
              name="username"
              autoComplete="username"
              placeholder="ระบุชื่อผู้ใช้"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              disabled={loading}
              aria-invalid={Boolean(errors.username)}
            />
            {errors.username && <ErrorMain errortext={errors.username} />}
          </div>

          <label className="text-mainblue font-semibold" htmlFor="password">
            รหัสผ่าน
          </label>
          <div className="-mt-[13px]">
            <div className="relative h-10 mt-[2px]">
              <InputPasswordMain
                id="password"
                name="password"
                autoComplete="current-password"
                placeholder="ระบุรหัสผ่าน"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                passwordShown={passwordShown}
                disabled={loading}
                aria-invalid={Boolean(errors.password)}
              />
              <button
                type="button"
                className="absolute right-[12px] top-1/2 -translate-y-1/2 text-maingray"
                aria-label={passwordShown ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                onClick={() => setPasswordShown((current) => !current)}
                disabled={loading}
              >
                {passwordShown ? (
                  <ICON_EYE_CLOSE className="w-5 h-5" />
                ) : (
                  <ICON_EYE className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && <ErrorMain errortext={errors.password} />}
          </div>

          {requestError && (
            <div className="text-red-500 text-sm text-center" role="alert">
              {requestError}
            </div>
          )}

          <div className="flex justify-center items-center text-base gap-1.5">
            <Checkbox
              type="checkbox"
              color="cyan"
              checked={isPolicyAccepted}
              onChange={() => setPolicyAccepted((current) => !current)}
              aria-label="ยอมรับนโยบายความเป็นส่วนตัว"
              disabled={loading}
            />
            <div className="text-maingray -ml-2">นโยบายความเป็นส่วนตัว</div>
            <button
              type="button"
              className="text-blue400 hover:font-semibold underline cursor-pointer"
            >
              อ่านเพิ่มเติม
            </button>
          </div>

          <MTButton
            type="submit"
            loading={loading}
            text="เข้าสู่ระบบ"
            color="bg-maincyan"
            rounded="rounded-[10px]"
            width="w-full"
          />
          <MTButton
            type="button"
            text="กลับไปหน้าเข้าสู่ระบบหลัก"
            color="bg-white"
            border="border border-maincyan"
            colortext="text-maincyan group-hover:text-white"
            hovercolor="hover:bg-maincyan group"
            rounded="rounded-[10px]"
            width="w-full"
            onClick={() => navigate("/login")}
            disabled={loading}
          />
        </form>

        <PopupMain
          open={isPolicyAlertOpen}
          text={'กรุณา "ยอมรับ" นโยบายความเป็นส่วนตัวก่อน'}
          img="/images/bad_face.png"
          onClick={() => setPolicyAlertOpen(false)}
        />
      </div>
    </div>
  );
};

export default UsernameLoginPage;
